import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useParams, useLocation, useSearch, Link } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetRecipe,
  useUpdateRecipe,
  useDeleteRecipe,
  getListRecipesQueryKey,
  getGetRecipeQueryKey,
  getGetRecipeQueryOptions,
  type RecipeDetail,
  type UpdateRecipeRequest,
} from "@workspace/api-client-react";
import {
  ChevronLeft,
  ChevronRight,
  Save,
  Trash2,
  Loader2,
  AlertCircle,
  BookOpen,
  Camera,
  Image as ImageIcon,
  X,
  CheckCircle2,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { HeroImageUploadDialog } from "@/components/HeroImageUploadDialog";
import { ImageViewer } from "@/components/validate/ImageViewer";
import {
  IngredientsEditor,
  type LocalIngredient,
} from "@/components/validate/IngredientsEditor";
import { NutritionPanel, type NutritionData } from "@/components/validate/NutritionPanel";
import { ScaleControl } from "@/components/validate/ScaleControl";
import { cn } from "@/lib/utils";
import { TagSelector } from "@/components/TagSelector";

// ─── Constants ───────────────────────────────────────────────────────────────

const CATEGORIES = [
  "Main Dish", "Side", "Bread", "Beverage", "Dessert", "Appetizer",
  "Soup/Stew", "Salad", "Sauce/Condiment", "Snack", "Breakfast", "Other",
];
const DIFFICULTIES = ["Easy", "Medium", "Hard"];
const SOURCE_TYPES = ["handwritten", "printed", "mixed"];

// ─── Types ────────────────────────────────────────────────────────────────────

type LeftPanelTab = "hero" | "scan";

interface FormState {
  title: string;
  authorSource: string;
  category: string;
  cuisine: string;
  prepTime: string;
  cookTime: string;
  totalTime: string;
  yieldServings: string;
  difficulty: string;
  directions: string;
  servingSuggestions: string;
  handwrittenNotes: string;
  transcriberNotes: string;
  sourceType: string;
  ingredients: LocalIngredient[];
  tags: string[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function recipeToForm(recipe: RecipeDetail): FormState {
  return {
    title: recipe.title ?? "",
    authorSource: recipe.authorSource ?? "",
    category: recipe.category ?? "",
    cuisine: recipe.cuisine ?? "",
    prepTime: recipe.prepTime ?? "",
    cookTime: recipe.cookTime ?? "",
    totalTime: recipe.totalTime ?? "",
    yieldServings: recipe.yieldServings ?? "",
    difficulty: recipe.difficulty ?? "",
    directions: recipe.directions ?? "",
    servingSuggestions: recipe.servingSuggestions ?? "",
    handwrittenNotes: recipe.handwrittenNotes ?? "",
    transcriberNotes: recipe.transcriberNotes ?? "",
    sourceType: recipe.sourceType ?? "",
    tags: ((recipe as RecipeDetail & { tags?: { slug: string }[] }).tags ?? []).map((t) => t.slug),
    ingredients: (recipe.ingredients ?? []).map((ing, idx) => ({
      _key: ing.id ? `db-${ing.id}` : `idx-${idx}-${Date.now()}`,
      id: ing.id,
      sortOrder: ing.sortOrder,
      amount: ing.amount ?? null,
      unit: ing.unit ?? null,
      item: ing.item,
      prepNotes: ing.prepNotes ?? null,
      sectionHeader: ing.sectionHeader ?? null,
    })),
  };
}

function formToUpdate(form: FormState): UpdateRecipeRequest {
  return {
    title: form.title || undefined,
    authorSource: form.authorSource || null,
    category: form.category || null,
    cuisine: form.cuisine || null,
    prepTime: form.prepTime || null,
    cookTime: form.cookTime || null,
    totalTime: form.totalTime || null,
    yieldServings: form.yieldServings || null,
    difficulty: form.difficulty || null,
    directions: form.directions || undefined,
    servingSuggestions: form.servingSuggestions || null,
    handwrittenNotes: form.handwrittenNotes || null,
    transcriberNotes: form.transcriberNotes || null,
    sourceType: form.sourceType || null,
    tags: form.tags,
    ingredients: form.ingredients.map((ing) => ({
      sortOrder: ing.sortOrder,
      amount: ing.amount,
      unit: ing.unit,
      item: ing.item || (ing.sectionHeader ? "" : "Untitled"),
      prepNotes: ing.prepNotes,
      sectionHeader: ing.sectionHeader,
    })),
  } as UpdateRecipeRequest & { tags: string[] };
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function RecipeEditorPage() {
  const { id } = useParams<{ id: string }>();
  const recipeId = parseInt(id ?? "", 10);
  const [, navigate] = useLocation();
  const search = useSearch();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // Mobile tab toggle — defaults to "recipe" so users land on something they can edit.
  const [mobileTab, setMobileTab] = useState<"recipe" | "image">("recipe");

  // ── Import batch navigation (from=scan|text&batch=id1,id2,...) ─────────────
  const searchParams = new URLSearchParams(search);
  const importSource = searchParams.get("from");
  const fromImport = importSource === "scan" || importSource === "text";
  const importHref = importSource === "text" ? "/recipe/paste" : "/recipe/scan";
  const importLabel = importSource === "text" ? "Paste" : "Scan";
  const batchIds: number[] = (searchParams.get("batch") ?? "")
    .split(",")
    .map((s) => parseInt(s, 10))
    .filter((n) => !isNaN(n));
  const batchIdx = batchIds.indexOf(recipeId);
  const hasBatchPrev = fromImport && batchIdx > 0;
  const hasBatchNext =
    fromImport && batchIdx >= 0 && batchIdx < batchIds.length - 1;
  const navBatch = useCallback((targetId: number) => {
    navigate(
      `/recipe/${targetId}/edit?from=${importSource}&batch=${batchIds.join(",")}`,
    );
  }, [navigate, importSource, batchIds]);

  // ── Data fetching ──────────────────────────────────────────────────────────
  const { data: recipe, isLoading, refetch } = useGetRecipe(
    recipeId,
    { query: { ...getGetRecipeQueryOptions(recipeId), enabled: !isNaN(recipeId) } }
  );

  // ── Left panel state ───────────────────────────────────────────────────────
  const sourceImages = recipe?.sourceImages ?? [];
  const hasScanImages = sourceImages.length > 0;
  const heroUrl = (recipe as (RecipeDetail & { heroImagePath?: string | null }) | undefined)?.heroImagePath
    ? `/api/recipes/hero-images/file/${((recipe as RecipeDetail & { heroImagePath?: string | null }).heroImagePath ?? "").replace("hero-images/", "")}`
    : null;
  const hasHero = !!heroUrl;

  // Default tab: no-hero + has-scan → scan; otherwise hero
  const [activeTab, setActiveTab] = useState<LeftPanelTab>("hero");
  const [tabInitialized, setTabInitialized] = useState(false);

  useEffect(() => {
    if (recipe && !tabInitialized) {
      if (!hasHero && hasScanImages) {
        setActiveTab("scan");
      } else {
        setActiveTab("hero");
      }
      setTabInitialized(true);
    }
  }, [recipe, hasHero, hasScanImages, tabInitialized]);

  // Reset tab init on recipe change
  useEffect(() => {
    setTabInitialized(false);
  }, [recipeId]);

  // ── Draggable divider ──────────────────────────────────────────────────────
  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const [leftPct, setLeftPct] = useState(42);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pct = ((e.clientX - rect.left) / rect.width) * 100;
      setLeftPct(Math.max(25, Math.min(70, pct)));
    };
    const onUp = () => { dragging.current = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  // ── Form state ─────────────────────────────────────────────────────────────
  const [form, setForm] = useState<FormState | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editRevisionRef = useRef(0);
  const savedRevisionRef = useRef(-1);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Nutrition + scale ──────────────────────────────────────────────────────
  const [nutritionData, setNutritionData] = useState<NutritionData | null>(null);
  const [scaleFactor, setScaleFactor] = useState(1);
  useEffect(() => { setScaleFactor(1); }, [recipeId]);

  // ── Dialog state ───────────────────────────────────────────────────────────
  const [deleteDialog, setDeleteDialog] = useState(false);

  // Sync form when recipe loads
  useEffect(() => {
    if (recipe) {
      setForm(recipeToForm(recipe));
      setIsDirty(false);
      setSaveState("idle");
    }
  }, [recipe?.id]);

  // ── Mutations ──────────────────────────────────────────────────────────────
  const updateMutation = useUpdateRecipe({
    mutation: {
      onSuccess: (updated) => {
        queryClient.setQueryData(getGetRecipeQueryKey(updated.id), updated);
        queryClient.invalidateQueries({ queryKey: getListRecipesQueryKey() });
        setSaveState("saved");
        if (editRevisionRef.current === savedRevisionRef.current) {
          setIsDirty(false);
        }
        if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
        savedTimerRef.current = setTimeout(() => setSaveState("idle"), 3000);
      },
      onError: () => setSaveState("error"),
    },
  });

  const deleteMutation = useDeleteRecipe({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListRecipesQueryKey() });
        toast({ title: "Recipe deleted" });
        navigate("/");
      },
    },
  });

  // ── Auto-save (2s debounce) ────────────────────────────────────────────────
  useEffect(() => {
    if (!isDirty || !recipeId || !form) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setSaveState("idle");
    autoSaveTimerRef.current = setTimeout(() => {
      setSaveState("saving");
      savedRevisionRef.current = editRevisionRef.current;
      updateMutation.mutate({ id: recipeId, data: formToUpdate(form) });
    }, 2000);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [form, isDirty, recipeId]);

  const saveNow = useCallback(() => {
    if (!recipeId || !form || !isDirty) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setSaveState("saving");
    savedRevisionRef.current = editRevisionRef.current;
    updateMutation.mutate({ id: recipeId, data: formToUpdate(form) });
  }, [recipeId, form, isDirty]);

  const finalizeImport = useCallback(async () => {
    if (!recipeId || !form) {
      navigate(`/recipe/${recipeId}`);
      return;
    }
    if (isDirty) {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      setSaveState("saving");
      savedRevisionRef.current = editRevisionRef.current;
      await new Promise<void>((resolve) => {
        updateMutation.mutate(
          { id: recipeId, data: formToUpdate(form) },
          { onSettled: () => resolve() }
        );
      });
    }
    toast({ title: "Recipe added to your cookbook!" });
    navigate(`/recipe/${recipeId}`);
  }, [recipeId, form, isDirty, updateMutation, navigate, toast]);

  // ── Field update helper ────────────────────────────────────────────────────
  const setField = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    editRevisionRef.current++;
    setIsDirty(true);
  }, []);

  // ── Apply extraction from ImageViewer ─────────────────────────────────────
  const handleApplyExtraction = useCallback(
    (dataType: string, parsed: Record<string, unknown>, _highlightId: number) => {
      if (!form) return;
      let updates: Partial<FormState> = {};
      let toastMsg = "";

      switch (dataType) {
        case "notes":
        case "other": {
          const text = String(parsed.text ?? "");
          updates.handwrittenNotes = form.handwrittenNotes
            ? `${form.handwrittenNotes}\n${text}` : text;
          toastMsg = "Notes added";
          break;
        }
        case "directions": {
          const dir = String(parsed.directions ?? "");
          updates.directions = form.directions
            ? `${form.directions}\n${dir}` : dir;
          toastMsg = "Directions added";
          break;
        }
        case "title":
          updates.title = String(parsed.title ?? "");
          toastMsg = "Title updated";
          break;
        case "author":
          updates.authorSource = String(parsed.author_source ?? "");
          toastMsg = "Author/source updated";
          break;
        case "yield":
          updates.yieldServings = String(parsed.yield_servings ?? "");
          toastMsg = "Yield/servings updated";
          break;
        case "times": {
          if (parsed.prep_time != null) updates.prepTime = String(parsed.prep_time);
          if (parsed.cook_time != null) updates.cookTime = String(parsed.cook_time);
          if (parsed.total_time != null) updates.totalTime = String(parsed.total_time);
          toastMsg = "Times updated";
          break;
        }
        case "ingredients": {
          const newIngs = (parsed.ingredients as Array<{
            amount?: string | null; unit?: string | null; item?: string;
            prep_notes?: string | null; section_header?: string | null;
          }>) ?? [];
          const lastSort = Math.max(0, ...form.ingredients.map((i) => i.sortOrder ?? 0));
          const added = newIngs.map((ing, idx) => ({
            _key: `highlight-${Date.now()}-${idx}`,
            id: undefined as number | undefined,
            sortOrder: lastSort + idx + 1,
            amount: ing.amount ?? null,
            unit: ing.unit ?? null,
            item: ing.item ?? "Ingredient",
            prepNotes: ing.prep_notes ?? null,
            sectionHeader: ing.section_header ?? null,
          }));
          updates.ingredients = [...form.ingredients, ...added];
          toastMsg = `${added.length} ingredient${added.length !== 1 ? "s" : ""} added`;
          break;
        }
        default:
          return;
      }

      if (Object.keys(updates).length > 0) {
        setForm((prev) => (prev ? { ...prev, ...updates } : prev));
        editRevisionRef.current++;
        setIsDirty(true);
        toast({ title: toastMsg });
      }
    },
    [form, toast]
  );

  // ─── Loading / error states ────────────────────────────────────────────────

  if (isNaN(recipeId)) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center">
          <AlertCircle className="w-12 h-12 text-muted-foreground" />
          <p className="text-muted-foreground">Invalid recipe ID.</p>
          <Link href="/"><Button variant="outline">Back to Cookbook</Button></Link>
        </div>
      </Layout>
    );
  }

  if (isLoading || !form) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (!recipe) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center">
          <AlertCircle className="w-12 h-12 text-muted-foreground" />
          <p className="text-muted-foreground">Recipe not found.</p>
          <Link href="/"><Button variant="outline">Back to Cookbook</Button></Link>
        </div>
      </Layout>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Layout>
      {/* Toolbar + (on mobile) tab toggle. On phone-sized viewports we make
          this region sticky just below the page header so the toolbar and the
          Image/Recipe tab toggle stay reachable while the form scrolls. */}
      <div
        className={cn(
          "flex-shrink-0 bg-background",
          isMobile && "sticky z-40"
        )}
        style={isMobile ? { top: "64px" } : undefined}
      >
      {/* ── Top bar ───────────────────────────────────────────────────────── */}
      <div className="border-b bg-background px-4 py-2 flex items-center gap-3 flex-wrap gap-y-2">
        {/* Navigation breadcrumb */}
        <div className="flex items-center gap-1.5 text-sm">
          {fromImport && batchIds.length === 0 ? (
            <Link
              href={importHref}
              className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors group"
            >
              <ChevronLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" />
              Back to {importLabel}
            </Link>
          ) : (
            <Link
              href={`/recipe/${recipeId}`}
              className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors group"
            >
              <ChevronLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" />
              Back to Recipe
            </Link>
          )}
          <span className="text-muted-foreground/40">·</span>
          <Link href="/" className="text-muted-foreground hover:text-foreground transition-colors">
            Cookbook
          </Link>
        </div>

        {/* Batch navigation from scan or pasted-text imports */}
        {fromImport && batchIds.length > 1 && (
          <div className="flex items-center gap-1 border rounded-md overflow-hidden text-xs">
            <button
              className="px-2 py-1 hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-0.5"
              disabled={!hasBatchPrev}
              onClick={() => navBatch(batchIds[batchIdx - 1])}
              title="Previous new recipe"
            >
              <ChevronLeft className="w-3 h-3" /> Previous New Recipe
            </button>
            <span className="px-2 py-1 border-x text-muted-foreground">
              {batchIdx + 1} / {batchIds.length}
            </span>
            <button
              className="px-2 py-1 hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-0.5"
              disabled={!hasBatchNext}
              onClick={() => navBatch(batchIds[batchIdx + 1])}
              title="Next new recipe"
            >
              Next New Recipe <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Save state indicator */}
        <div className="ml-auto flex items-center gap-2 text-xs">
          {saveState === "saving" && (
            <span className="flex items-center gap-1 text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Saving…
            </span>
          )}
          {saveState === "saved" && (
            <span className="flex items-center gap-1 text-green-600">
              <Save className="h-3 w-3" /> Saved
            </span>
          )}
          {saveState === "error" && (
            <span className="flex items-center gap-1 text-destructive">
              <AlertCircle className="h-3 w-3" /> Save failed
            </span>
          )}
          {isDirty && saveState === "idle" && (
            <span className="text-muted-foreground">Unsaved…</span>
          )}
        </div>

        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1.5"
          disabled={!isDirty || updateMutation.isPending}
          onClick={saveNow}
        >
          <Save className="h-3.5 w-3.5" />
          Save
        </Button>

        {fromImport ? (
          <Button
            size="sm"
            className="h-7 gap-1.5 bg-green-600 hover:bg-green-700 text-white"
            disabled={updateMutation.isPending}
            onClick={finalizeImport}
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            Done Reviewing
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 gap-1.5 text-muted-foreground hover:text-foreground"
            onClick={() => navigate(`/recipe/${recipeId}`)}
          >
            <BookOpen className="h-3.5 w-3.5" />
            View
          </Button>
        )}
      </div>

      {/* Mobile tab toggle — pinned with the toolbar so it stays reachable
          while the recipe form scrolls. The Image tab is always available so
          users can add a cover photo even when no scan images exist. */}
      {isMobile && (
        <div className="bg-background/95 backdrop-blur border-b">
          <div className="flex" role="tablist" aria-label="Recipe editor panel">
            <button
              type="button"
              role="tab"
              aria-selected={mobileTab === "recipe"}
              onClick={() => setMobileTab("recipe")}
              className={cn(
                "flex-1 py-2.5 text-sm font-medium border-b-2 transition-colors",
                mobileTab === "recipe"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              Recipe details
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mobileTab === "image"}
              onClick={() => setMobileTab("image")}
              className={cn(
                "flex-1 py-2.5 text-sm font-medium border-b-2 transition-colors",
                mobileTab === "image"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              Image
            </button>
          </div>
        </div>
      )}
      </div>{/* end sticky toolbar wrapper */}

      {/* ── Two-panel body ─────────────────────────────────────────────────── */}
      <div
        ref={containerRef}
        className={cn(
          "flex-1 min-h-0 overflow-hidden",
          isMobile ? "flex flex-col" : "flex"
        )}
        style={
          isMobile
            ? { height: "calc(100dvh - 168px)", minHeight: "360px" }
            : { height: "calc(100vh - 104px)" }
        }
      >
        {/* Left panel — image (hero / scan).
            On mobile we hide it via `display: none` when the Recipe tab is active,
            but only mount the ImageViewer when the Image tab is active so its
            initial fit/centering logic gets a non-zero sizing context. */}
        <div
          className={cn(
            "overflow-hidden flex flex-col",
            isMobile
              ? cn("flex-1 min-h-0 w-full", mobileTab !== "image" && "hidden")
              : "flex-shrink-0"
          )}
          style={!isMobile ? { width: `${leftPct}%` } : undefined}
        >
          {/* Tab bar */}
          <div className="flex border-b flex-shrink-0">
            <button
              className={cn(
                "flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px",
                activeTab === "hero"
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
              onClick={() => setActiveTab("hero")}
            >
              <Camera className="h-3.5 w-3.5" />
              Hero Photo
            </button>
            {hasScanImages && (
              <button
                className={cn(
                  "flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px",
                  activeTab === "scan"
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                )}
                onClick={() => setActiveTab("scan")}
              >
                <ImageIcon className="h-3.5 w-3.5" />
                Original Scan
                <span className="text-xs bg-muted text-muted-foreground rounded px-1.5 py-0.5 ml-0.5">
                  {sourceImages.length}
                </span>
              </button>
            )}
          </div>

          {/* Tab content */}
          <div className="flex-1 min-h-0 overflow-hidden">
            {activeTab === "hero" && (
              <div className="h-full flex flex-col items-center justify-center p-6 gap-4 overflow-auto">
                {heroUrl ? (
                  <div className="relative w-full max-w-lg group rounded-xl overflow-hidden border shadow-sm">
                    <img
                      src={heroUrl}
                      alt={recipe.title}
                      className="w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-xl" />
                    <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                      <HeroImageUploadDialog
                        recipeId={recipeId}
                        hasHero={true}
                        heroUrl={heroUrl}
                        onSuccess={() => refetch()}
                        trigger={
                          <button className="bg-black/50 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-xl hover:bg-black/70 transition-colors flex items-center gap-1.5">
                            <Camera className="w-3.5 h-3.5" />
                            Change Photo
                          </button>
                        }
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-4 text-center">
                    <div className="w-24 h-24 rounded-2xl bg-muted flex items-center justify-center">
                      <Camera className="w-10 h-10 text-muted-foreground/40" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">No cover photo yet</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Add a beautiful photo to represent this recipe
                      </p>
                    </div>
                    <HeroImageUploadDialog
                      recipeId={recipeId}
                      hasHero={false}
                      heroUrl={null}
                      onSuccess={() => refetch()}
                      trigger={
                        <button className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-muted-foreground/30 text-sm text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors">
                          <Camera className="w-4 h-4" />
                          Add a cover photo
                        </button>
                      }
                    />
                  </div>
                )}
              </div>
            )}

            {activeTab === "scan" && hasScanImages && (!isMobile || mobileTab === "image") && (
              <ImageViewer
                images={sourceImages}
                recipeId={recipeId}
                onApplyExtraction={handleApplyExtraction}
              />
            )}
          </div>
        </div>

        {/* Draggable divider — desktop only */}
        {!isMobile && (
          <div
            className="w-1 cursor-col-resize bg-border hover:bg-primary/40 transition-colors flex-shrink-0 select-none"
            onMouseDown={() => { dragging.current = true; }}
          />
        )}

        {/* Right panel: recipe form. Always mounted on mobile so unsaved edits
            and scroll position persist across tab switches. */}
        <div
          className={cn(
            "min-w-0 flex flex-col",
            isMobile
              ? cn("flex-1 min-h-0 w-full", mobileTab !== "recipe" && "hidden")
              : "flex-1 overflow-hidden"
          )}
        >
          <ScrollArea className="flex-1">
            <div className="p-5 space-y-5 pb-8">

              {/* Hero Image (right-panel section) */}
              <div className="border rounded-lg p-3 bg-muted/30">
                <Label className="text-xs text-muted-foreground mb-2 block">Cover Photo</Label>
                {hasHero ? (
                  <div className="flex items-center gap-3">
                    <img
                      src={heroUrl!}
                      alt="Cover"
                      className="w-16 h-16 rounded-md object-cover border flex-shrink-0"
                    />
                    <div className="flex flex-col gap-1.5">
                      <HeroImageUploadDialog
                        recipeId={recipeId}
                        hasHero={true}
                        heroUrl={heroUrl}
                        onSuccess={() => refetch()}
                        trigger={
                          <Button size="sm" variant="outline" className="h-7 text-xs gap-1.5">
                            <Camera className="w-3 h-3" /> Change Photo
                          </Button>
                        }
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={async () => {
                          try {
                            await fetch(`/api/recipes/${recipeId}/hero-image`, { method: "DELETE" });
                            await refetch();
                            toast({ title: "Cover photo removed" });
                          } catch {
                            toast({ title: "Failed to remove photo", variant: "destructive" });
                          }
                        }}
                      >
                        <X className="w-3 h-3" /> Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <HeroImageUploadDialog
                    recipeId={recipeId}
                    hasHero={false}
                    heroUrl={null}
                    onSuccess={() => refetch()}
                    trigger={
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1.5">
                        <Camera className="w-3 h-3" /> Add Cover Photo
                      </Button>
                    }
                  />
                )}
              </div>

              {/* Title */}
              <div>
                <Label className="text-xs text-muted-foreground">Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setField("title", e.target.value)}
                  className="mt-1 text-base font-semibold font-serif"
                  placeholder="Recipe title…"
                />
              </div>

              {/* Author / Category / Cuisine */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Author / Source</Label>
                  <Input
                    value={form.authorSource}
                    onChange={(e) => setField("authorSource", e.target.value)}
                    className="mt-1 text-sm"
                    placeholder="e.g. Aunt Carol"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Category</Label>
                  <Select
                    value={form.category || "none"}
                    onValueChange={(v) => setField("category", v === "none" ? "" : v)}
                  >
                    <SelectTrigger className="mt-1 h-9 text-sm">
                      <SelectValue placeholder="Category…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Cuisine</Label>
                  <Input
                    list="cuisine-suggestions-editor"
                    value={form.cuisine}
                    onChange={(e) => setField("cuisine", e.target.value)}
                    className="mt-1 text-sm"
                    placeholder="e.g. American"
                  />
                  <datalist id="cuisine-suggestions-editor">
                    <option value="American" /><option value="Italian" /><option value="Mexican" />
                    <option value="Chinese" /><option value="Japanese" /><option value="French" />
                    <option value="Indian" /><option value="Thai" /><option value="Mediterranean" />
                    <option value="Greek" /><option value="Spanish" /><option value="Korean" />
                  </datalist>
                </div>
              </div>

              {/* Tags */}
              <div>
                <Label className="text-xs text-muted-foreground">Tags</Label>
                <div className="mt-1">
                  <TagSelector
                    selected={form.tags}
                    onChange={(next) => setField("tags", next)}
                    placeholder="Add a tag (e.g. weeknight, vegan)…"
                  />
                </div>
              </div>

              {/* Times + Yield + Difficulty + Source type */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Prep Time</Label>
                  <Input
                    value={form.prepTime}
                    onChange={(e) => setField("prepTime", e.target.value)}
                    className="mt-1 text-sm" placeholder="15 min"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Cook Time</Label>
                  <Input
                    value={form.cookTime}
                    onChange={(e) => setField("cookTime", e.target.value)}
                    className="mt-1 text-sm" placeholder="30 min"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Total Time</Label>
                  <Input
                    value={form.totalTime}
                    onChange={(e) => setField("totalTime", e.target.value)}
                    className="mt-1 text-sm" placeholder="45 min"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Yield / Servings</Label>
                  <Input
                    value={form.yieldServings}
                    onChange={(e) => setField("yieldServings", e.target.value)}
                    className="mt-1 text-sm" placeholder="Serves 4"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Difficulty</Label>
                  <Select
                    value={form.difficulty || "none"}
                    onValueChange={(v) => setField("difficulty", v === "none" ? "" : v)}
                  >
                    <SelectTrigger className="mt-1 h-9 text-sm">
                      <SelectValue placeholder="Difficulty…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {DIFFICULTIES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Source Type</Label>
                  <div className="mt-2 flex flex-col gap-1">
                    {SOURCE_TYPES.map((s) => (
                      <label key={s} className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                          type="radio" name="sourceType" value={s}
                          checked={form.sourceType === s}
                          onChange={() => setField("sourceType", s)}
                          className="accent-primary"
                        />
                        <span className="capitalize">{s}</span>
                      </label>
                    ))}
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-muted-foreground">
                      <input
                        type="radio" name="sourceType" value=""
                        checked={!form.sourceType}
                        onChange={() => setField("sourceType", "")}
                        className="accent-primary"
                      />
                      <span>—</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Ingredients */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-xs text-muted-foreground">
                    Ingredients ({form.ingredients.length})
                  </Label>
                </div>
                <ScaleControl scale={scaleFactor} onChange={setScaleFactor} />
                <div className="mt-2">
                  <IngredientsEditor
                    ingredients={form.ingredients}
                    onChange={(ings) => setField("ingredients", ings)}
                    scaleFactor={scaleFactor}
                  />
                </div>
              </div>

              {/* Directions */}
              <div>
                <Label className="text-xs text-muted-foreground">Directions</Label>
                <Textarea
                  value={form.directions}
                  onChange={(e) => setField("directions", e.target.value)}
                  className="mt-1 text-sm min-h-[140px] font-mono leading-relaxed"
                  placeholder="Step-by-step instructions…"
                />
              </div>

              {/* Serving suggestions */}
              <div>
                <Label className="text-xs text-muted-foreground">Serving Suggestions</Label>
                <Textarea
                  value={form.servingSuggestions}
                  onChange={(e) => setField("servingSuggestions", e.target.value)}
                  className="mt-1 text-sm min-h-[60px]"
                  placeholder="Optional…"
                />
              </div>

              {/* Handwritten notes */}
              <div>
                <Label className="text-xs text-muted-foreground">
                  Handwritten Notes <span className="text-xs italic">(from original)</span>
                </Label>
                <Textarea
                  value={form.handwrittenNotes}
                  onChange={(e) => setField("handwrittenNotes", e.target.value)}
                  className="mt-1 text-sm min-h-[60px] italic bg-yellow-50 border-yellow-200 text-yellow-900 placeholder:text-yellow-400"
                  placeholder="Notes visible on the original image…"
                />
              </div>

              {/* Transcriber notes */}
              <div>
                <Label className="text-xs text-muted-foreground">
                  Your Notes <span className="text-xs italic">(personal)</span>
                </Label>
                <Textarea
                  value={form.transcriberNotes}
                  onChange={(e) => setField("transcriberNotes", e.target.value)}
                  className="mt-1 text-sm min-h-[60px]"
                  placeholder="Your personal notes…"
                />
              </div>

              {/* Action buttons */}
              <div className="border-t pt-4 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 text-destructive hover:bg-destructive/10 border-destructive/40 ml-auto"
                  disabled={!recipeId}
                  onClick={() => setDeleteDialog(true)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete Recipe
                </Button>
              </div>

              {/* Recipe ID footnote */}
              <p className="text-xs text-muted-foreground/60">
                Recipe ID #{recipeId} · Auto-saves after 2 seconds
              </p>

              {/* Nutrition estimate panel */}
              <NutritionPanel
                recipeId={recipeId}
                ingredients={form.ingredients}
                scaleFactor={scaleFactor}
                onNutritionLoaded={setNutritionData}
              />
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* ── Delete dialog ──────────────────────────────────────────────────── */}
      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this recipe?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{recipe.title}</strong> will be permanently deleted. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              onClick={() => {
                deleteMutation.mutate({ id: recipeId });
                setDeleteDialog(false);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
